import React from 'react';
import styled from 'styled-components';
import { colors } from "../../utils/colors";
import * as TypographyStyles from '../TypographyStyles';
import { TransferHistory } from './Types/transferHistory';
import { slateColors } from '../ContentPages/AppLandingPage/styles';
import { useTranslation } from 'react-i18next';

const ContainerCard = styled('div')`
    display: flex;
    padding: 1.5rem;
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
    align-self: start;
    border-radius: 2.5rem;
    border: 1px solid ${slateColors[200]};
    background: white;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
    box-sizing: border-box;
    transition: all 0.3s;
    
    &:hover {
        box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
        border-color: ${slateColors[300]};
    }
`

const CardHeader = styled('div')`
    display: flex;
    align-items: flex-start;
    gap: 4px;
`

const Chart = styled.div`
display: flex;
flex-direction: column;
align-items: flex-start;
gap: 12px;
align-self: stretch;
`;

const Cohort = styled.div`
display: flex;
align-items: center;
gap: 14px;
align-self: stretch;
`;

const HorizontalBar = styled.div`
display: flex;
height: 16px;
align-items: center;
flex: 1 0 0;
border-radius: 999px;
background: ${colors.grey300};
`;

interface BarFillProps {
    width: string;
    color: string;  // Add color prop
}

const BarFill = styled.div<BarFillProps>`
    height: 16px;
    border-radius: 999px;
    background-color: ${({ color }) => color};  // Use color prop for background color
    width: ${({ width }) => width};
`;

const CategoryText = styled(TypographyStyles.BodySRegular)`
color: ${slateColors[800]};
width: 72px;
`;

const ValueText = styled(TypographyStyles.BodySRegular)`
color: ${slateColors[800]};
width: 25px;
text-align: right;
`;



interface TransferDistributionCardProps {
    transferHistoryData: TransferHistory[];
}

// Function to map category names to colors
const getBarColor = (label: string, t: any) => {
    const cancelled = t('statistics.transferCategories.cancelled');
    const missed = t('statistics.transferCategories.missed');
    const zeroToFour = t('statistics.transferCategories.zeroToFour');
    const fiveToNine = t('statistics.transferCategories.fiveToNine');
    const tenToFourteen = t('statistics.transferCategories.tenToFourteen');
    const fifteenPlus = t('statistics.transferCategories.fifteenPlus');
    
    switch (label) {
        case cancelled: return colors.red;
        case missed: return colors.red;
        case zeroToFour: return colors.red;
        case fiveToNine: return colors.orange;
        case tenToFourteen: return colors.green;
        case fifteenPlus: return colors.green;
        default: return slateColors[200];
    }
};

const TransferDistributionCard: React.FC<TransferDistributionCardProps> = ({ transferHistoryData }) => {
    const { t } = useTranslation();

    // Cancelled, missed, 0-4, 5-9, 10-14, 15+
    const buckets = [0, 0, 0, 0, 0, 0];
    transferHistoryData.forEach(entry => {
        if (entry.arrivalCancelled || entry.departureCancelled) {
            buckets[0] += 1;
            return;
        }
        if (entry.secondsToChange != null) {
            const transferMinutes = entry.secondsToChange / 60;

            if (transferMinutes <= 0) buckets[1] += 1;
            else if (transferMinutes <= 4) buckets[2] += 1;
            else if (transferMinutes <= 9) buckets[3] += 1;
            else if (transferMinutes <= 14) buckets[4] += 1;
            else buckets[5] += 1;
        }
    });

    const totalEntries = buckets.reduce((sum, count) => sum + count, 0);
    const percentages = totalEntries === 0
        ? buckets.map(() => 0)
        : buckets.map(count => (count / totalEntries) * 100);

    const xAxisLabels = [
        t('statistics.transferCategories.cancelled'),
        t('statistics.transferCategories.missed'),
        t('statistics.transferCategories.zeroToFour'),
        t('statistics.transferCategories.fiveToNine'),
        t('statistics.transferCategories.tenToFourteen'),
        t('statistics.transferCategories.fifteenPlus'),
    ];

    const showCancelled = buckets[0] > 0;
    const visiblePercentages = showCancelled ? percentages : percentages.slice(1);
    const visibleLabels = showCancelled ? xAxisLabels : xAxisLabels.slice(1);

    return (
        <ContainerCard>
            <CardHeader>
                <TypographyStyles.BodyLSemibold color={slateColors[900]}>
                    {t('statistics.transferTimeDistribution')}
                </TypographyStyles.BodyLSemibold>
                <TypographyStyles.BodyLRegular color={slateColors[600]}>
                    {t('statistics.last30Days')}
                </TypographyStyles.BodyLRegular>
            </CardHeader>
            <Chart>
                {visiblePercentages.map((percentage, index) => (
                    <Cohort key={visibleLabels[index]}>
                        <CategoryText>{visibleLabels[index]}</CategoryText>
                        <HorizontalBar>
                            <BarFill 
                                width={`${percentage}%`} 
                                color={getBarColor(visibleLabels[index], t)}
                            />
                        </HorizontalBar>
                        <ValueText>{percentage.toFixed(0)}%</ValueText>
                    </Cohort>
                ))}
            </Chart>
        </ContainerCard>
    );
};

export default TransferDistributionCard
